/**
 * Create Template Dialog
 * 
 * Dialog for creating a new template draft:
 * - If cloneFromKey is provided, clones from existing template
 * - Otherwise creates a brand new template
 */

import { useState } from 'react';
import { useInspectionTemplateEditor } from '@/hooks/useInspectionTemplateEditor';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';

interface CreateTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shopId: string;
  cloneFromKey: string | null;
  onSuccess: (templateId: string) => void;
}

export function CreateTemplateDialog({
  open,
  onOpenChange,
  shopId,
  cloneFromKey,
  onSuccess,
}: CreateTemplateDialogProps) {
  const editor = useInspectionTemplateEditor(shopId);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [templateKey, setTemplateKey] = useState('');

  const isCloning = !!cloneFromKey;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const key = isCloning ? cloneFromKey : templateKey.toLowerCase().replace(/\s+/g, '_');
      const result = await editor.createDraft(key, name, description || undefined);
      
      if (result.template_id) {
        onSuccess(result.template_id);
        // Reset form
        setName('');
        setDescription('');
        setTemplateKey('');
      }
    } catch (error) {
      // Error handled by hook
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isCloning ? 'Create New Version' : 'Create New Template'}
          </DialogTitle>
          <DialogDescription>
            {isCloning
              ? 'This will create a new draft version that you can customize.'
              : 'Create a new inspection template from scratch.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Template Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Full Vehicle Inspection"
              required
            />
          </div>

          {!isCloning && (
            <div className="space-y-2">
              <Label htmlFor="templateKey">Template Key</Label>
              <Input
                id="templateKey"
                value={templateKey}
                onChange={(e) => setTemplateKey(e.target.value)}
                placeholder="e.g., full_vehicle_inspection"
                pattern="^[a-z][a-z0-9_]*$"
                title="Lowercase letters, numbers, and underscores only. Must start with a letter."
                required
              />
              <p className="text-xs text-muted-foreground">
                Unique identifier. Lowercase, underscores only.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="description">Description (optional)</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this template covers..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={editor.isCreatingDraft}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={editor.isCreatingDraft || !name || (!isCloning && !templateKey)}>
              {editor.isCreatingDraft && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isCloning ? 'Create Draft' : 'Create Template'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
